import ProductOptions from '@/components/LegacyMiniCart/productOptions';
import Price from '@/components/Price';
import { TENTH_ANNIVERSARY_KEY_CHAINS_SKU } from '@/constants/10th-anniversary-key-chains-sku';
import { useItem } from '@/hooks/MiniCart/useItem';
import { getCartItemIsOutOfStock } from '@/lib/get-cart-item-is-out-of-stock';
import resourceUrl from '@/util/makeUrl';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo, useRef, useState } from 'react';

const Item = props => {
  const {
    classes: propClasses,
    product,
    uid,
    quantity,
    configurable_options,
    custom_info,
    select_options,
    handleRemoveItem,
    prices,
    closeMiniCart,
    configurableThumbnailSource,
    storeUrlSuffix,
    robes_gifts_customization,
    item,
    onUpdateItemQuantity,
    isUpdatingQuantity,
  } = props;

  const talonProps = useItem({
    item,
    product,
    select_options,
    configurableThumbnailSource,
    configurable_options,
    uid,
    handleRemoveItem,
  });

  const { isOutOfStock } = getCartItemIsOutOfStock({
    item: item,
  });

  const { customizable_options_data, imageSource, isDeleting, removeItem, customOptions, handleShowCustomOptions } =
    talonProps;

  const { options } = product;

  const is10thAnniversaryKeyChains = product.sku === TENTH_ANNIVERSARY_KEY_CHAINS_SKU;

  const [selectedQuantity, setSelectedQuantity] = useState(quantity);
  const propsRef = useRef({
    quantity,
  });

  const handleSelectedQuantityChange = e => {
    const nextQuantity = e.target.value;

    setSelectedQuantity(nextQuantity);
    onUpdateItemQuantity(nextQuantity);
  };

  if (propsRef.current.quantity !== quantity) {
    propsRef.current.quantity = quantity;
    setSelectedQuantity(quantity);
  }

  const { available_product } = item || {};

  const { url_key: availableUrlKey, name: availableName } = available_product || {};

  const itemLink = useMemo(
    () => resourceUrl(`/${availableUrlKey || product.url_key}${storeUrlSuffix || ''}`),
    [availableUrlKey, product.url_key, storeUrlSuffix],
  );

  const textCustomizeOptions = useMemo(() => {
    return robes_gifts_customization?.map((item, index) => {
      if (item.label === 'Photo') {
        return null;
      }

      return (
        <div
          className='text-sm text-8B857D'
          key={index}
        >
          <span>
            {item.label} :&nbsp;
            {item.value}
          </span>
        </div>
      );
    });
  }, [robes_gifts_customization]);

  const photoCustomizationOption = useMemo(() => {
    const find = robes_gifts_customization.find(temp => temp.label === 'Photo');

    if (find) {
      return (
        <div className='flex break-all'>
          <span>Photo :&nbsp;</span>
          <span>
            <img
              className='h-[50px] w-[50px]'
              src={find.value}
              alt=''
            />
          </span>
        </div>
      );
    }

    return null;
  }, [robes_gifts_customization]);

  const maybeCustomizeOptions =
    robes_gifts_customization?.length > 0 ? (
      <>
        {textCustomizeOptions}
        {photoCustomizationOption}
      </>
    ) : null;

  const maybeCrossedPrice = useMemo(() => {
    const price = item.product?.price?.regularPrice?.amount;

    if (price?.value && price?.value > prices.price.value) {
      return (
        <span className='line-through'>
          <Price
            value={price.value}
            currencyCode={price.currency}
          />
        </span>
      );
    }

    return null;
  }, [item.product?.price?.regularPrice?.amount, prices.price.value]);

  return (
    <div
      className={clsx(
        'relative flex items-start gap-x-2.5 py-2.5',
        isDeleting || isUpdatingQuantity ? 'opacity-50' : '',
        'border-b-1 border-solid border-E7DED5',
      )}
    >
      {is10thAnniversaryKeyChains ? (
        <div className='flex-none'>
          <Image
            alt={product.name}
            className='h-[160px] w-[120px] object-contain'
            width={120}
            height={160}
            src={imageSource}
          />
        </div>
      ) : (
        <Link
          href={itemLink}
          onClick={closeMiniCart}
          className='block flex-none'
        >
          <Image
            alt={product.name}
            className='h-[160px] w-[120px] object-contain'
            width={120}
            height={160}
            src={imageSource}
          />
        </Link>
      )}

      <div className='flex-1'>
        {is10thAnniversaryKeyChains ? (
          <div className='text-base leading-[22px] text-80583F'> {availableName || product.name}</div>
        ) : (
          <Link
            className='text-base leading-[22px] text-80583F'
            href={itemLink}
            onClick={closeMiniCart}
          >
            {availableName || product.name}
          </Link>
        )}
        <div>
          <ProductOptions
            options={customizable_options_data?.length > 0 ? customizable_options_data : options}
            custom_info={custom_info}
            showToggleBtn
            classes={{
              options: clsx('text-sm leading-5 text-8B857D'),
            }}
          />
          <div className='flex items-center text-sm leading-5 text-8B857D'>
            <span>Price :&nbsp;</span>
            <span>
              <Price
                currencyCode={prices.price.currency}
                value={prices.price.value}
              />
            </span>
            {maybeCrossedPrice ? <>&nbsp;</> : null}
            <span>{maybeCrossedPrice}</span>
          </div>
          {maybeCustomizeOptions}
          <div className='mt-2.5 text-sm leading-5 text-8B857D'>QTY</div>
          <div className='mt-[5px] flex items-center justify-between'>
            {is10thAnniversaryKeyChains ? (
              <span>1</span>
            ) : (
              <div className='relative'>
                <select
                  value={selectedQuantity}
                  onChange={handleSelectedQuantityChange}
                  disabled={isDeleting || isUpdatingQuantity}
                  className={clsx(
                    'h-9 w-[76px] ',
                    'rounded-[3px] border-1 border-solid border-[#ABA6A0]',
                    'px-[9px]',
                    'appearance-none',
                  )}
                >
                  {Array.from(
                    {
                      length: 10,
                    },
                    (_, i) => {
                      return (
                        <option
                          key={i}
                          label={i + 1}
                          value={i + 1}
                        />
                      );
                    },
                  )}
                </select>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='11'
                  height='6'
                  fill='none'
                  viewBox='0 0 11 6'
                  className='pointer-events-none absolute right-[9px] top-1/2 -translate-y-1/2'
                >
                  <path
                    fill='#80583F'
                    d='M10.5.5H8.43L5.5 3.43 2.57.5H.5l5 5z'
                  />
                  <path
                    fill='#000'
                    fillOpacity='0.2'
                    d='M10.5.5H8.43L5.5 3.43 2.57.5H.5l5 5z'
                  />
                </svg>
              </div>
            )}

            {is10thAnniversaryKeyChains ? null : (
              <button
                onClick={removeItem}
                type='button'
                disabled={isDeleting || isUpdatingQuantity}
                className='py-2 text-sm leading-5 text-80583F underline'
              >
                Remove
              </button>
            )}
          </div>
          {isOutOfStock ? (
            <div className='max-w-max rounded-[3px] bg-E7DED5 px-2.5 py-0.5 text-sm leading-5 text-80583F'>
              Out-of-stock
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
    </div>
  );
};

export default Item;
